import { Plus, Trash2 } from 'lucide-react';
import {
  Button,
  Input,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@vnetwork/ui/primitives';
import { Field } from '@vnetwork/ui/forms';
import { SectionCard, type CreateState, type Tag } from './shared';

interface SshKey {
  id: string | number;
  name?: string;
}

/** Authentication Method — SSH key or password. */
export function AuthCard({
  form,
  patch,
  sshKeys,
  showErrors,
}: {
  form: CreateState;
  patch: (p: Partial<CreateState>) => void;
  sshKeys: SshKey[];
  showErrors: boolean;
}) {
  return (
    <SectionCard
      title="Authentication Method"
      description="Select the method you prefer for logging into your instance from outside the network."
    >
      <div style={{ display: 'flex', gap: 'var(--vn-space-2)', marginBottom: 'var(--vn-space-3)' }}>
        {(['sshkey', 'password'] as const).map((opt) => (
          <Button
            key={opt}
            type="button"
            size="sm"
            variant={form.auth === opt ? 'primary' : 'secondary'}
            onClick={() => patch({ auth: opt })}
          >
            {opt === 'sshkey' ? 'SSH key' : 'Password'}
          </Button>
        ))}
      </div>
      {form.auth === 'sshkey' ? (
        <Field label="SSH key">
          {() => (
            <Select value={form.sshkey} onValueChange={(v) => patch({ sshkey: v })}>
              <SelectTrigger style={{ maxWidth: 420 }}>
                <SelectValue placeholder={sshKeys.length ? 'Select an SSH key' : 'No SSH keys — add one first'} />
              </SelectTrigger>
              <SelectContent>
                {sshKeys.map((k) => (
                  <SelectItem key={k.id} value={String(k.id)}>
                    {k.name || k.id}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </Field>
      ) : (
        <Field label="Password" required>
          {(p) => (
            <Input
              {...p}
              type="password"
              value={form.password}
              onChange={(e) => patch({ password: e.target.value })}
              style={{ maxWidth: 420 }}
              invalid={showErrors && form.password.length < 8}
            />
          )}
        </Field>
      )}
    </SectionCard>
  );
}

/** Tags — key/value labels. */
export function TagsCard({ tags, onChange }: { tags: Tag[]; onChange: (t: Tag[]) => void }) {
  const set = (i: number, p: Partial<Tag>) => onChange(tags.map((t, idx) => (idx === i ? { ...t, ...p } : t)));
  return (
    <SectionCard
      title="Tags"
      description="Tags are labels you assign to resources, consisting of a key-value pair."
      footer={
        <span style={{ color: 'var(--vn-fg-muted)', fontSize: 'var(--vn-font-size-sm)' }}>
          You can add up to {Math.max(0, 50 - tags.length)} more tags.
        </span>
      }
    >
      <div style={{ display: 'grid', gap: 'var(--vn-space-2)' }}>
        {tags.map((t, i) => (
          <div key={i} style={{ display: 'flex', gap: 'var(--vn-space-2)', alignItems: 'center', flexWrap: 'wrap' }}>
            <Input value={t.key} placeholder="key" onChange={(e) => set(i, { key: e.target.value })} style={{ flex: 1, minWidth: 'min(180px, 100%)' }} />
            <Input value={t.value} placeholder="value" onChange={(e) => set(i, { value: e.target.value })} style={{ flex: 2, minWidth: 'min(180px, 100%)' }} />
            <Button type="button" variant="ghost" size="sm" onClick={() => onChange(tags.filter((_, idx) => idx !== i))}>
              <Trash2 size={15} />
            </Button>
          </div>
        ))}
        <div>
          <Button
            type="button"
            variant="secondary"
            size="sm"
            startIcon={<Plus size={14} />}
            disabled={tags.length >= 50}
            onClick={() => onChange([...tags, { key: '', value: '' }])}
          >
            Add new tag
          </Button>
        </div>
      </div>
    </SectionCard>
  );
}
